/**
 * AlertBadgeToggle Component
 *
 * Collapsible alert level badges toggle that shows either:
 * - Collapsed: "X levels" count with down chevron
 * - Expanded: Individual alert badges with up chevron
 *
 * @module RecipientItem/AlertBadgeToggle
 */

import { Badge, Icon, InlineStack } from '@shopify/polaris';
import { ChevronDownIcon, ChevronUpIcon } from '@shopify/polaris-icons';

import type { AlertLevel } from './types';
import { getAlertLevelIcon, getAlertLevelTone, getAlertLevelLabel } from './utils';
import { ARIA_LABELS } from './constants';

export interface AlertBadgeToggleProps {
  /** Alert levels to display */
  alertLevels: AlertLevel[];
  /** Whether the badges are expanded */
  isExpanded: boolean;
  /** Whether to show the pop-in animation */
  showPopAnimation?: boolean;
  /** Callback when toggle is clicked */
  onToggle: () => void;
}

/**
 * Collapsible toggle for alert level badges
 *
 * When collapsed, shows a count badge (e.g., "3 levels").
 * When expanded, shows individual badges for each alert level with staggered pop-in animation.
 */
export function AlertBadgeToggle({
  alertLevels,
  isExpanded,
  showPopAnimation = false,
  onToggle,
}: AlertBadgeToggleProps) {
  // Don't render if no alert levels
  if (alertLevels.length === 0) {
    return null;
  }

  const levelText = alertLevels.length === 1 ? 'level' : 'levels';

  return (
    <button
      type="button"
      onClick={onToggle}
      className={`alert-badges-toggle ${showPopAnimation ? 'recipient-alert-levels-pop' : ''}`}
      aria-expanded={isExpanded}
      aria-label={isExpanded ? ARIA_LABELS.COLLAPSE_ALERTS : ARIA_LABELS.EXPAND_ALERTS}
    >
      <InlineStack gap="100" blockAlign="center" wrap={false}>
        {isExpanded ? (
          // Expanded: show all badges with pop-in animation
          <>
            {alertLevels.map((level, index) => (
              <span
                key={level}
                className="alert-badge-item"
                data-animation-delay={index}
              >
                <Badge
                  tone={getAlertLevelTone(level)}
                  icon={getAlertLevelIcon(level)}
                  size="small"
                >
                  {getAlertLevelLabel(level)}
                </Badge>
              </span>
            ))}
            <span className="alert-badge-chevron">
              <Icon source={ChevronUpIcon} tone="subdued" />
            </span>
          </>
        ) : (
          // Collapsed: show count badge with chevron
          <>
            <span className="alert-badge-count">
              {`${alertLevels.length} ${levelText}`}
            </span>
            <span className="alert-badge-chevron">
              <Icon source={ChevronDownIcon} tone="subdued" />
            </span>
          </>
        )}
      </InlineStack>
    </button>
  );
}
